import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorType } from "@/types";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useGetIncomeCategorListQuery } from "@/services/incomeCategoryApi";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { displayValue, formatToPKR, roundToTwoDecimals } from "@/lib/helper";
import { status } from "@/lib/constants/global";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import ProductForm from "../modal/form";
import { Product, ProductType } from "@/types/system-setting/product";
import { useDeleteProductMutation, useGetAllProductsQuery, useUpdateProductMutation } from "@/services/productApi";
import { ProductStatus } from "@/types/system-setting/product";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "products",
};

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
}

export default function ProductsTableView() {
  const product = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).product ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: productsData,
    isLoading,
    refetch,
  } = useGetAllProductsQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );
  const [updateProduct] = useUpdateProductMutation();

  const { data: incomeCatData } = useGetIncomeCategorListQuery(orgId);

  const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);

  const { data: priceMasterData } = useGetPriceMastersQuery({
    org_id: orgId,
    query: "item_type=product",
  });

  const priceMasterMap = React.useMemo(() => {
    const map: Record<number, number> = {};
    if (Array.isArray((priceMasterData as any)?.data)) {
      (priceMasterData as any).data.forEach((rule: any) => {
        map[rule.id] = rule.price;
      });
    }
    return map;
  }, [priceMasterData]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");

  const productTableData = React.useMemo(() => {
    return Array.isArray(productsData?.data) ? productsData?.data : [];
  }, [productsData]);

  const { toast } = useToast();

  const [data, setData] = useState<Product | undefined>(undefined);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleStatusChange = async (payload: Product) => {
    console.log("Payload from status", payload)
    try {
      const resp = await updateProduct({
        ...payload,
        type: payload.type.toLowerCase() as ProductType,
      }).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Product Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ?? (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleEditProduct = (data: Product) => {
    setData(data);
    setAction("edit");
    setIsDialogOpen(true);
  };
  const [deleteProduct, { isLoading: deleteLoading }] =
    useDeleteProductMutation();

  const handleDeleteProduct = async (data: Product) => {
    try {
      const resp = await deleteProduct({ product_id: data.id!, org_id: data.org_id! }).unwrap();
      if (resp) {
        toast({
          variant: "success",
          title: "Product Deleted Successfully",
        });


        setSearchCriteria({
          limit: 10,
          offset: 0,
          sort_order: "desc",
          sort_key: "id",
          type: "products",
        })
        setFilterData({})

        refetch()

      }
      return;
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  }

  const actionsColumn: ColumnDef<Product> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={product}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditProduct}
        handleDelete={handleDeleteProduct}
      />
    ),
  };

  const columns: ColumnDef<Product>[] = [
    {
      accessorKey: "sku",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="SKU"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.sku}`.length > 8
                          ? `${row.original.sku}`.substring(0, 8) + "..."
                          : `${row.original.sku}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">
                    {displayValue(`${row?.original?.sku}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },

      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "product_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Product Name"
          sortKey="product_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.product_name}`.length > 25
                          ? `${row.original.product_name}`.substring(0, 25) +
                          "..."
                          : `${row.original.product_name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">
                    {displayValue(`${row?.original?.product_name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "quantity",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Qty"
          sortKey="quantity"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{displayValue(row?.original?.quantity)}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "min_limit",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Min Limit"
          sortKey="min_limit"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <p>{displayValue(row?.original?.min_limit)}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "income_category_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Income Category"
          sortKey="income_category_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        
        return (
          <span className="text-nowrap ">{`${displayValue(row?.original?.income_category_name)}`}</span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "tax_group",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax Group"
          sortKey="tax_group"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        
        return (
          <span className="text-nowrap ">{row.original.tax_group && displayValue(row.original.tax_group.name)}</span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "price_master_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Price"
          sortKey="price_master_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const { price_master_id, flexible_price } = row.original;
        const price = price_master_id ? priceMasterMap[price_master_id] : undefined;
        return (
          <span className="text-nowrap">
            {flexible_price ? "Flexible Price" : (price != null ? formatToPKR(price) : "—")}
          </span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "cost",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Cost"
          sortKey="cost"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const { cost } = row.original;
        return <span>{formatToPKR(cost as number)}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },

    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Product Type"
          sortKey="type"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const { type } = row.original;
        return (
          <span className="capitalize">{`${displayValue(type.replace("_", " "))}`}</span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value = (row.original?.status as unknown as ProductStatus) ?? "inactive";
        const statusLabel = status.find((r) => r.value === value);
        return (
          <Select
            defaultValue={value}
            onValueChange={(newValue) =>
              handleStatusChange({
                ...row.original,
                status: newValue as unknown as ProductStatus
              })
            }
            disabled={product === "read"}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Status" className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  />
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {status.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(product !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: productTableData as Product[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
    setData(undefined);
  };

  const handleFilterChange = (
    field: string,
    value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
  ) => {
    let newValue: string | number | number[] | React.ChangeEvent<HTMLInputElement>;

    // Check if the value is coming from an input event or passed directly
    if (typeof value === "object" && "target" in value) {
      newValue = value.target.value;
    } else {
      newValue = value;
    }

    if (field === "discount_percentage") {
      newValue = Math.min(Number(newValue), 100); // Capping the discount percentage to 100
    }

    // Update the filter state based on field and value
    setFilterData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
  ];

  const totalRecords = productsData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {product !== "read" && (
            <Button
              className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              Create New
            </Button>
          )}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="hidden md:block rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (product !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEditProduct(row.original)
                      }
                    }}
                    className={product !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : productTableData && productTableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No Products found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : productTableData && productTableData.length ? (
          productTableData.map((row: Product) => {
            const { price_master_id, flexible_price } = row;
            const price = price_master_id ? priceMasterMap[price_master_id] : undefined;
            const statusLabel = status.find(
              (r) => r.value === ((row.status as unknown as string) ?? "inactive")
            );
            return (
              <div
                key={row.id}
                className="border border-border rounded-md p-3 space-y-2 bg-white"
                onClick={() => {
                  if (product !== "read") {
                    handleEditProduct(row);
                  }
                }}
              >
                {/* Header row: name + status dot + actions */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {displayValue(row.product_name)}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{displayValue(row.sku)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`${statusLabel?.color} rounded-full w-2.5 h-2.5 shrink-0`}></span>
                    <div onClick={(e) => e.stopPropagation()}>
                      {product !== "read" && (
                        <DataTableRowActions
                          access={product}
                          data={row}
                          refetch={refetch}
                          handleEdit={handleEditProduct}
                          handleDelete={handleDeleteProduct}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <div className="text-gray-500">Type</div>
                  <div className="text-right capitalize">
                    {displayValue(row.type.replace("_", " "))}
                  </div>

                  <div className="text-gray-500">Qty</div>
                  <div className="text-right">{displayValue(row.quantity)}</div>

                  <div className="text-gray-500">Min Limit</div>
                  <div className="text-right">{displayValue(row.min_limit)}</div>

                  <div className="text-gray-500">Price</div>
                  <div className="text-right">
                    {flexible_price ? "Flexible" : price != null ? formatToPKR(price) : "—"}
                  </div>

                  <div className="text-gray-500">Cost</div>
                  <div className="text-right">{formatToPKR(row.cost as number)}</div>

                  <div className="text-gray-500">Income Category</div>
                  <div className="text-right truncate">{displayValue(row.income_category_name)}</div>

                  <div className="text-gray-500">Tax Group</div>
                  <div className="text-right">{row.tax_group ? displayValue(row.tax_group.name) : "—"}</div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No records found.
          </div>
        )}
      </div>

      {/* pagination */}
      {productTableData && productTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <ProductForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        refetch={refetch}
        data={data}
        setData={setData}
      />
    </div>
  );
}
